import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { Formik } from "formik";
import { Lock, Mail } from "lucide-react";
import { AuthContext } from "../context/AuthContext";

import "../firebase";

import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { BACKEND_URL } from "../config";

import useFetch from "use-http";
import { useTranslation } from "react-i18next";
import { usePostHog } from "@posthog/react";

import { AuthShell } from "../v2/auth/AuthShell";
import { AuthAlert, AuthField, AuthSubmit } from "../v2/auth/AuthForm";

function Login() {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { post, response } = useFetch(BACKEND_URL, {
    cachePolicy: "no-cache",
  });
  const { auth, setAuthData, matchedDomain } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();


  // SECURITY (SECURITY_PLAN.md F32): purge any password this app stored before
  // the fix. Without this, everyone who ever ticked "remember me" keeps their
  // plaintext password on disk forever, since nothing else would clear it.
  useEffect(() => {
    localStorage.removeItem("userPassword");
  }, []);

  useEffect(() => {
    if (auth?.data !== null && auth?.data?.verified) {
      const isFirstVisit = localStorage.getItem("isFirstVisit");
      if (!isFirstVisit) {
        localStorage.setItem("isFirstVisit", "true");
        window.location.href = "/app/profiles";
      } else {
        navigate("/app/dashboard");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async ({ email, password, isRemember }) => {
    // SECURITY (SECURITY_PLAN.md F32): "remember me" used to write the user's
    // PASSWORD to localStorage in plain text. Never store a password in the
    // browser: unlike a token it does not expire, users reuse it on other
    // services, it survives on shared machines indefinitely, and any script on
    // the page can read it with one line. The browser's own password manager
    // already does this properly (OS keychain, encrypted, device-unlock) —
    // see the autoComplete attributes on the email/password inputs below.
    //
    // This checkbox controls ONE thing, deliberately: whether the email is
    // prefilled next time. It intentionally does NOT touch session length.
    // Sign-in stays persistent for everyone (the Firebase SDK default keeps
    // the session in IndexedDB across browser restarts), because users expect
    // to stay logged in the way they do on Gmail — closing a tab should not
    // sign you out. Do not add setPersistence(browserSessionPersistence) here:
    // it was considered and rejected as bad UX, not overlooked.
    if (isRemember) {
      localStorage.setItem("userEmail", email);
    } else {
      localStorage.removeItem("userEmail");
    }
    try {
      setLoading(true);

      const firebaseAuth = getAuth();
      signInWithEmailAndPassword(firebaseAuth, email, password)
        .then(async (userCredential) => {
          const user = userCredential.user;

          if (!user?.emailVerified) {
            navigate("/signup-success");
            return;
          }

          await post(`/user/check-account-active`, {
            user_token: await user.getIdToken(),
          });
          const resJson = await response.json();

          if (resJson.status === "pending") {
            setLoading(false);
            setError(t("auth.login.errors.accountPending"));
          } else if (resJson.status === "error") {
            if (
              resJson.message ===
              "We couldn't find your account. Please sign up again."
            ) {
              await signOut(firebaseAuth);
              setTimeout(() => {
                navigate("/create-account");
              }, 3000);
            } else if (
              resJson.message ===
              "Your account has been blocked. Please contact support."
            ) {
              await signOut(firebaseAuth);
              setLoading(false);
              setError(resJson.message);
            } else if (resJson.message === "maximumActivation") {
              setError(t("auth.login.errors.maximumActivation"));
              signOut(firebaseAuth);
              setLoading(false);
            }
          } else {
            posthog?.identify(user.uid, {
              email: user?.email,
              name: user?.displayName,
            });
            setAuthData({
              email: user?.email,
              verified: user?.emailVerified,
              jwt: user?.accessToken,
              id: user?.uid,
              name: user?.displayName,
            });
            setTimeout(() => {
              setLoading(false);
              if (resJson.data.num_of_profiles > 0) {
                navigate("/app/dashboard");
              } else {
                navigate("/app/profiles");
              }
            }, 3000);
          }
        })
        .catch((err) => {
          const errorCode = err?.code;
          const errorMap = {
            "auth/wrong-password": "wrongPassword",
            "auth/user-not-found": "noAccountFound",
            "auth/network-request-failed": "networkError",
            "auth/too-many-requests": "tooManyRequests",
            "auth/invalid-email": "invalidEmail",
            "auth/user-disabled": "userDisabled",
            "auth/invalid-credential": "invalidCredential",
            "auth/invalid-login-credentials": "invalidCredential",
          };
          const errorKey = errorMap[errorCode] || "genericError";
          setError(t(`auth.login.errors.${errorKey}`));
          setLoading(false);
        });
    } catch ({ message }) {
      setError(message);
      console.error("[login Error]", message);
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow={t("auth.login.eyebrow", "Welcome back")}
      title={t("auth.login.title", {
        defaultValue: "Sign in to {{app}}",
        app: matchedDomain?.app_name || "HyperClapper",
      })}
      subtitle={t("auth.login.subtitle", "Pick up right where you left off.")}
      footer={
        <>
          {t("auth.login.needAnAccount")}{" "}
          <Link
            to="/create-account"
            className="font-semibold text-[var(--accent-bright)] hover:underline"
          >
            {t("auth.login.signUp")}
          </Link>
        </>
      }
    >
      <AuthAlert>{error}</AuthAlert>

      <Formik
        initialValues={{
          email: localStorage.getItem("userEmail") || "",
          // SECURITY (SECURITY_PLAN.md F32): never prefill from stored
          // credentials. The browser's password manager fills this field
          // itself, from the OS keychain, which is what should own it.
          password: "",
          isRemember: localStorage.getItem("userEmail") ? true : false,
        }}
        onSubmit={submit}
        validationSchema={yup.object().shape({
          email: yup
            .string()
            .email()
            .required(t("auth.login.errors.emailRequired")),
          password: yup
            .string()
            .required(t("auth.login.errors.passwordRequired")),
        })}
      >
        {({
          values,
          handleChange,
          errors,
          setFieldTouched,
          touched,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className="space-y-4">
            <AuthField
              label={t("auth.login.emailAddress")}
              name="email"
              type="email"
              icon={Mail}
              placeholder={t("auth.login.enterYourEmail")}
              autoComplete="username"
              value={values.email}
              onChange={handleChange("email")}
              onBlur={() => setFieldTouched("email")}
              error={touched.email && errors.email}
            />

            <AuthField
              label={t("auth.login.password")}
              name="password"
              type="password"
              icon={Lock}
              placeholder={t("auth.login.enterYourPassword")}
              autoComplete="current-password"
              value={values.password}
              onChange={handleChange("password")}
              onBlur={() => setFieldTouched("password")}
              error={touched.password && errors.password}
              trailing={
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-medium text-[var(--accent-bright)] hover:underline"
                >
                  {t("auth.login.forgotPassword")}
                </Link>
              }
            />

            <label className="flex items-center gap-2 text-[13px] text-muted">
              <input
                type="checkbox"
                name="isRemember"
                checked={values.isRemember}
                onChange={handleChange("isRemember")}
                className="h-4 w-4 rounded border-[var(--border-strong)] accent-[var(--accent)]"
              />
              {t("auth.login.rememberMe")}
            </label>

            <AuthSubmit loading={loading || isSubmitting}>
              {t("auth.login.signIn")}
            </AuthSubmit>

            {/* One short line, as in the template: "By continuing you agree
                to our Terms and Privacy Policy." */}
            <p className="text-center text-[12px] leading-relaxed text-muted-2">
              {t("auth.login.legalPrefix", "By continuing you agree to our")}{" "}
              <a
                href="https://hyperclapper.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground/70 underline transition-colors hover:text-foreground"
              >
                {t("auth.login.terms", "Terms")}
              </a>{" "}
              {t("auth.login.and", "and")}{" "}
              <a
                href="https://hyperclapper.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground/70 underline transition-colors hover:text-foreground"
              >
                {t("auth.createAccount.privacyPolicy") || "Privacy Policy"}
              </a>
              {t("auth.login.legalSuffix", ".")}
            </p>
          </form>
        )}
      </Formik>
    </AuthShell>
  );
}

export default Login;
